import { computed, inject } from '@angular/core';
import { tapResponse } from '@ngrx/operators';
import { patchState, signalStore, withComputed, withMethods, withState } from '@ngrx/signals';
import { rxMethod } from '@ngrx/signals/rxjs-interop';
import { pipe, switchMap, tap } from 'rxjs';

import { MeetingPointer } from '../models';
import { MeetingPointerService } from '../services';

import { EntityCollectionState } from '~/shared/models';

export type MeetingPointersState = EntityCollectionState<MeetingPointer>;

const initialState: MeetingPointersState = {
  data: [],
  isLoading: false,
  hasFetchedOnce: false,
  error: null
};

export const MeetingPointersStore = signalStore(
  { providedIn: 'root' },

  withState(initialState),
  withComputed((state) => ({
    dataSource: computed(
      (): MeetingPointersState => ({
        data: state.data(),
        isLoading: state.isLoading(),
        hasFetchedOnce: state.hasFetchedOnce(),
        error: state.error()
      })
    )
  })),
  withMethods((store, service = inject(MeetingPointerService)) => {
    const methods = {
      fetch: rxMethod<void>(
        pipe(
          tap(() =>
            patchState(store, {
              isLoading: true,
              error: null
            })
          ),
          switchMap(() =>
            service.getMeetingPointers().pipe(
              tapResponse({
                next: (data) =>
                  patchState(store, {
                    data,
                    isLoading: false,
                    hasFetchedOnce: true
                  }),

                error: (err) => {
                  patchState(store, {
                    data: [],
                    isLoading: false,
                    hasFetchedOnce: store.hasFetchedOnce(),
                    error: 'Failed to load meetingPointers'
                  });
                  console.error(err);
                }
              })
            )
          )
        )
      ),

      set: (meetingPointers: MeetingPointer[]) => patchState(store, { data: meetingPointers })
    };

    return methods;
  }),
  withMethods((store) => ({
    isMeetingItemPointed: (meetingId: string, meetingItemId: string): boolean => {
      const data = store.data();
      const meetingItem = data.find((meetingItem) => meetingItem.meetingId.toString() === meetingId);

      if (!meetingItem) return null;

      const activeMeetingIds: string[] = (meetingItem.activeMeetingParentIds?.map((id) => id.toString()) || []).concat([
        meetingItem.activeAgendaPageId?.toString()
      ]);

      return !!activeMeetingIds.find((activeId) => activeId === meetingItemId);
    }
  }))
);
